// inplantsStore.tsx
import { makeAutoObservable, action, observable } from "mobx";
import { ILines, IModules, IStation, IPlants } from "../../../models/IUser";
import apiService from "../../../services/apiService";
import { logApiError } from "../../../utils/logApiError";
import { withoutUntouchedPlace } from "../../../utils/stationPlacePayload";

/**
 * `(7)` (`АРХ-Н36`): из строки опроса — только поля, которые в ней пришли.
 * Отсутствующее (`undefined`) не переносится, `null` переносится.
 */
function presentFields<T extends object>(row: T, keys: string[]): Partial<T> {
  const out: Record<string, unknown> = {};

  for (const key of keys) {
    const value = (row as Record<string, unknown>)[key];
    if (value !== undefined) out[key] = value;
  }

  return out as Partial<T>;
}

export class InplantsStore {
  // === Состояние ===

  // Приватное поле plant — контролируется через initialize()
  private _plant: IPlants | null = null;

  lines: ILines[] = [];
  modules: IModules[] = [];
  stations: IStation[] = [];

  // UI state
  isLoading = false;
  error: string | null = null;
  isPoll = true;
  isDev = false;
  //readonly isDev = process.env.NODE_ENV === "development";

  // Активный модуль (для popup)
  activeModule: IModules | null = null;

  // Активная линия (для формы редактирования)
  activeLine: ILines | null = null;

  // Активная станция (для popup)
  activeStation: IStation | null = null;

  // Авто-рефреш
  private autoRefreshInterval: NodeJS.Timeout | null = null;

  constructor() {
    makeAutoObservable(this);
    this.loadState();
  }

  // === LocalStorage methods ===

  loadState() {
    try {
      const saved = localStorage.getItem("inplantsStore");
      if (saved) {
        const { plant, lines, modules, stations } = JSON.parse(saved);
        this._plant = plant ?? null;
        this.lines = lines ?? [];
        this.modules = modules ?? [];
        this.stations = stations ?? [];
      }
    } catch (e) {
      logApiError("Failed to load inplantsStore state:", e);
    }
  }

  saveState() {
    try {
      localStorage.setItem(
        "inplantsStore",
        JSON.stringify({
          plant: this._plant,
          lines: this.lines,
          modules: this.modules,
          stations: this.stations,
        }),
      );
    } catch (e) {
      logApiError("Failed to save inplantsStore state:", e);
    }
  }

  // === Геттеры ===

  get plant(): IPlants | null {
    return this._plant;
  }

  get isInitialized(): boolean {
    return this._plant !== null && this._plant.id !== undefined;
  }

  // === Actions ===

  @action
  initialize(plant: IPlants) {
    this._plant = plant;
    this.saveState();
  }

  @action
  setLines(lines: ILines[]) {
    this.lines = lines.sort((a, b) => a.id - b.id);
  }

  @action
  setModules(modules: IModules[]) {
    this.modules = observable(modules.sort((a, b) => a.id - b.id));
  }

  @action
  setStations(stations: IStation[]) {
    this.stations = observable(
      stations.sort((a, b) => a.controller_address - b.controller_address),
    );
  }

  @action
  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  @action
  setError(error: string | null) {
    this.error = error;
  }

  @action
  setPoll(isPoll: boolean) {
    this.isPoll = isPoll;
  }

  @action
  setActiveModule(module: IModules | null) {
    this.activeModule = module;
    this.isPoll = module === null;
  }

  @action
  setActiveLine(line: ILines | null) {
    this.activeLine = line;
    this.isPoll = line === null;
  }

  @action
  setActiveStation(station: IStation | null) {
    this.activeStation = station;
    this.isPoll = station === null;
  }

  @action
  reset() {
    this._plant = null;
    this.lines = [];
    this.modules = [];
    this.stations = [];
    this.activeModule = null;
    this.activeLine = null;
    this.activeStation = null;
    this.isLoading = false;
    this.error = null;
    this.stopAutoRefresh();
  }

  // === API Methods ===

  async loadPlantData(plantId: number) {
    this.setLoading(true);
    try {
      const data = await apiService.fetchEntity(`/plantData/${plantId}`);

      this.setLines(data.data.lines);
      this.setModules(data.data.modules);
      this.setStations(data.data.stations);

      this.saveState();
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch plant data");
    } finally {
      this.setLoading(false);
    }
  }

  // === Polling: обновление только "живых" данных ===

  async pollPlantData(plantId: number) {
    try {
      const data = await apiService.fetchEntity(`/plantData/${plantId}/poll`);
      const pollData = data.data.data || data.data;
      this.applyPollData(pollData.lines, pollData.modules, pollData.stations);
    } catch (error: any) {
      this.setError(error.message || "Failed to poll plant data");
    }
  }

  @action
  applyPollData(
    pollLines: ILines[],
    pollModules: IModules[],
    pollStations: IStation[],
  ) {
    const updatedLines = this.lines.map((existing) => {
      const poll = pollLines.find((l) => l.id === existing.id);
      if (!poll) return existing;
      return {
        ...existing,
        current_limit_line: poll.current_limit_line,
        limit_A: poll.limit_A,
        limit_B: poll.limit_B,
        limit_C: poll.limit_C,
      };
    });

    const updatedModules = this.modules.map((existing) => {
      const poll = pollModules.find((m) => m.id === existing.id);
      if (!poll) return existing;
      return {
        ...existing,
        status: poll.status,
        value_A: poll.value_A,
        value_B: poll.value_B,
        value_C: poll.value_C,
        limit: poll.limit,
      };
    });

    const updatedStations = this.stations.map((existing) => {
      const poll = pollStations.find((s) => s.id === existing.id);
      if (!poll) return existing;
      // `(7)` (`АРХ-Н36`): поле, которого в ответе опроса нет, не затирается —
      // иначе «версия ПО» и доступность становились `undefined`. `null` в
      // ответе — значение («пусто»), его переносим.
      return {
        ...existing,
        ...presentFields(poll, [
          "online",
          "availability",
          "energyWh",
          "currentA",
          // `B.1` (`АУД-Н308`): опрос переносит и десятые — иначе после
          // «станция встала» карточка оставалась с целыми до перезагрузки.
          "current_dA",
          "acVersion",
          "pivVersion",
          "status_id",
          "state_id",
          "gps",
        ]),
      };
    });

    this.lines = updatedLines;
    this.modules = updatedModules;
    this.stations = updatedStations;
  }

  // === Линии: CRUD ===

  async createLine(data: Partial<ILines>) {
    try {
      if (this._plant?.id) {
        data.plantId = this._plant.id;
      }
      const newLine = await apiService.createEntity("/createLine", data);
      this.setLines([...this.lines, newLine.data]);
    } catch (error: any) {
      this.setError(error.message || "Failed to create line");
      throw error;
    }
  }

  async updateLine(data: Partial<ILines>) {
    try {
      const ID = data.id;
      if (!ID) throw new Error("Line ID is required");

      // Маршрут: PATCH /line/:id (как в старом store.tsx)
      const response = await apiService.patchEntity<ILines>("/line", ID, data);

      // Обновляем линию в списке
      this.setLines(
        this.lines.map((line) =>
          line.id === ID ? { ...line, ...data } : line,
        ),
      );
      return response;
    } catch (error: any) {
      this.setError(error.message || "Failed to update line");
      throw error;
    }
  }

  async deleteLine(id: number) {
    try {
      await apiService.deleteEntity<ApiResponse<any>>("/line", id);
      // Удаляем линию из списка
      this.setLines(this.lines.filter((line) => line.id !== id));
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete line";
      this.setError(message);
      throw error;
    }
  }

  async updateModule(
    module: Partial<IModules>,
    stations: Partial<IStation>[],
    childModules: Partial<IModules>[],
    modifiedKeys: string[],
  ) {
    // 🔍 Проверка: изменился ли ТОЛЬКО timer?
    const isTimerOnly =
      Array.isArray(modifiedKeys) &&
      modifiedKeys.length === 1 &&
      modifiedKeys[0] === "timer" &&
      module.timer !== undefined;

    if (isTimerOnly) {
      const res = await this.updateTimer(module.charge_id!, module.timer);
      if (this._plant?.id) {
        await this.loadPlantData(this._plant.id);
      }
      return { data: { isTimer: true, ...res } };
    }

    // 🔄 Обычный путь — обновление конфигурации
    const patchedStations = stations.map((s) => {
      // `АУД-Н343`: `place` — только если его правили (`__modified`).
      const station = withoutUntouchedPlace({ ...s });
      if ("Meters.meter_address" in station) {
        station.meter_address = station["Meters.meter_address"];
        delete station["Meters.meter_address"];
      }
      if ("Meters.meter_number" in station) {
        station.meter_number = station["Meters.meter_number"];
        delete station["Meters.meter_number"];
      }
      return station;
    });

    const payload = { module, stations: patchedStations, childModules, modifiedKeys };
    try {
      const response = await apiService.patchEntity<IModules>(
        "/module",
        module.id!,
        payload,
      );
      if (this._plant?.id) {
        await this.loadPlantData(this._plant.id);
      }
      return { isTimer: false, ...response };
    } catch (error: any) {
      this.setError(error.message || "Неизвестная ошибка");
      logApiError("Ошибка при обновлении модуля:", error);
      throw error;
    }
  }

  /**
   * Отдельный метод для сохранения изменений СМБ (дочерних) модулей
   * из таблицы в popupModule. Отправляет собственный запрос на бэкенд.
   */
  async updateChildModules(
    parentModule: Partial<IModules>,
    childModules: Partial<IModules>[],
    modifiedKeys: string[] = [],
  ) {
    const parentId = parentModule.id;
    if (!parentId) {
      throw new Error("Parent module ID is required");
    }

    try {
      // Бэкенд ожидает PATCH /mmb_module/:parentId
      // с payload { parentModule, childModules, modifiedKeys }
      const response = await apiService.patchEntity<IModules>(
        "/mmb_module",
        parentId,
        {
          parentModule,
          childModules,
          modifiedKeys,
        },
      );

      if (this._plant?.id) {
        await this.loadPlantData(this._plant.id);
      }

      return response;
    } catch (error: any) {
      this.setError(error.message || "Неизвестная ошибка");
      logApiError("Ошибка при обновлении дочерних модулей:", error);
      throw error;
    }
  }

  /**
   * Сохранение изменений модуля типа СМБ.
   * Бэкенд ожидает PATCH /smb_module/:id
   * с payload { module, stations, modifiedKeys }
   */
  async updateSmbModules(
    module: Partial<IModules>,
    stations: Partial<IStation>[],
    modifiedKeys: string[] = [],
  ) {
    // 🔍 Проверка: изменился ли ТОЛЬКО timer?
    const isTimerOnly =
      Array.isArray(modifiedKeys) &&
      modifiedKeys.length === 1 &&
      modifiedKeys[0] === "timer" &&
      module.timer !== undefined;

    if (isTimerOnly) {
      const res = await this.updateTimer(module.charge_id!, module.timer);
      if (this._plant?.id) {
        await this.loadPlantData(this._plant.id);
      }
      return { data: { isTimer: true, ...res } };
    }

    const patchedStations = stations.map((s) => {
      // `АУД-Н343`: `place` — только если его правили (`__modified`).
      const station = withoutUntouchedPlace({ ...s });
      if ("Meters.meter_address" in station) {
        station.meter_address = station["Meters.meter_address"];
        delete station["Meters.meter_address"];
      }
      if ("Meters.meter_number" in station) {
        station.meter_number = station["Meters.meter_number"];
        delete station["Meters.meter_number"];
      }
      return station;
    });

    const payload = { module, stations: patchedStations, modifiedKeys };
    try {
      const response = await apiService.patchEntity<IModules>(
        "/smb_module",
        module.id!,
        payload,
      );

      if (this._plant?.id) {
        await this.loadPlantData(this._plant.id);
      }

      return { isTimer: false, ...response };
    } catch (error: any) {
      this.setError(error.message || "Неизвестная ошибка");
      logApiError("Ошибка при обновлении СМБ модуля:", error);
      throw error;
    }
  }

  async updateTimer(identifier: string, timer: number) {
    try {
      const response = await apiService.POST<{
        status: "accepted" | "rejected";
      }>(`/set-timer`, { identifier, timer });
      return response.data;
    } catch (error: any) {
      this.setError(error.message || "Ошибка обновления таймера");
      throw error;
    }
  }

  async createModule(data: Partial<IModules>) {
    try {
      if (this._plant?.id) {
        data.plant_id = this._plant.id;
      }
      const response = await apiService.createEntity("/createModule", data);
      // Обновляем данные после создания
      if (this._plant?.id) {
        await this.loadPlantData(this._plant.id);
      }
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create module";
      this.setError(errorMessage);
      throw new Error(errorMessage);
    }
  }

  async deleteModule(id: number) {
    try {
      await apiService.deleteEntity<ApiResponse<any>>("/modules", id, {
        params: {
          force: true,
          reason: "admin",
        },
      });
      // Обновляем данные после удаления
      if (this._plant?.id) {
        this.loadPlantData(this._plant.id);
      }
    } catch (error: any) {
      this.setError(error.message || "Failed to delete module");
    }
  }

  async restartModule(identifier: string) {
    try {
      const response = await apiService.POST<{
        message: string;
        error?: string;
        status?: string;
      }>(`/restartModule`, { identifier });

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      // Обновляем данные после перезагрузки
      if (this._plant?.id) {
        this.loadPlantData(this._plant.id);
      }

      return response.data;
    } catch (error: any) {
      this.setError(error.message || "Ошибка перезагрузки модуля");
      throw error;
    }
  }

  // === Станции: CRUD и операции ===

  async updateStation(
    station: Partial<IStation>,
    position: "list" | "inplants",
  ) {
    const ID = station.id;

    try {
      const response = await apiService.patchEntity("/station", ID, station);

      // Обновляем станцию в списке
      this.setStations(
        this.stations.map((s) => (s.id === ID ? { ...s, ...station } : s)),
      );

      // Перезагружаем данные в зависимости от позиции
      if (position === "inplants" && this._plant?.id) {
        await this.loadPlantData(this._plant.id);
      }

      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Не удалось обновить станцию";
      this.setError(errorMessage);
      throw error;
    }
  }

  async restartStation(id: number, controller: number) {
    try {
      const response = await apiService.POST("/restart", { id, controller });
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.error ||
        error.message ||
        "Не удалось перезагрузить станцию";
      this.setError(message);
      throw new Error(message);
    }
  }

  async AvailabilityStation(
    id: number,
    moduleId: number,
    controller: number,
    availability: boolean,
  ) {
    try {
      const response = await apiService.POST("/availability", {
        id,
        moduleId,
        controller,
        availability,
      });
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.error ||
        error.message ||
        "Не удалось изменить доступность станции";
      this.setError(message);
      throw new Error(message);
    }
  }

  async UnlockConnector(id: number, controller: number) {
    try {
      const response = await apiService.POST("/unlockconnector", {
        id,
        controller,
      });
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.error ||
        error.message ||
        "Не удалось разблокировать разъём";
      this.setError(message);
      throw new Error(message);
    }
  }

  // === Auto Refresh ===

  startAutoRefresh(plantId: number, intervalMs: number = 3000) {
    this.stopAutoRefresh(); // Очищаем предыдущий интервал

    this.autoRefreshInterval = setInterval(() => {
      this.pollPlantData(plantId);
    }, intervalMs);
  }

  stopAutoRefresh() {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
      this.autoRefreshInterval = null;
    }
  }

  // === Helpers ===

  getLineStatus(lineId: number, modules: IModules[]): "online" | "offline" {
    const lineModules = modules.filter((mod) => mod.line_id === lineId);
    return lineModules.some((mod) => (mod as any).status === "online")
      ? "online"
      : "offline";
  }
}

export const inplantsStore = new InplantsStore();
